import { createSelector } from '@reduxjs/toolkit';

import { debug } from '@/shared/lib';

import { assetsModel } from '@/entities/assets';
import { findDCCTVolumeFile } from '@/entities/composed';
import { conditionModel } from '@/entities/condition';
import { reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import type { DCCTVolumeState, DCCTVolumeStatus } from '../config';
import { transformDataToMPRLauncherRequest } from '../utils/transformDataToMPRLauncherRequest';

export const selectMPREmbeddedLauncherRequest = (toothID: string) =>
  createSelector(
    toothModel.selectors.selectToothByID(toothID),
    assetsModel.selectors.selectMPRByToothID(toothID),
    reportsModel.selectors.selectCurrentReport,
    (tooth, CBCTGPMPRSubVolume, report) => {
      if (!CBCTGPMPRSubVolume) {
        debug('[MPREmbedded] Volume is undefined. Cannot start MPREmbedded');
        return null;
      }

      if (!tooth) {
        debug('[MPREmbedded] Tooth is undefined. Cannot start MPREmbedded');
        return null;
      }

      const cbctgp =
        report?.Specific.case === 'DataCBCTGP'
          ? report?.Specific.value
          : undefined;

      if (!cbctgp) {
        debug(
          '[MPREmbedded] Report DataCBCTGP is undefined. Cannot start MPREmbedded',
        );
        return null;
      }

      const mprLauncherRequest = transformDataToMPRLauncherRequest(
        cbctgp,
        [CBCTGPMPRSubVolume],
        tooth,
      );

      return mprLauncherRequest;
    },
  );

export const selectDCCTVolumeState = ({
  patientID,
  reportID,
}: {
  patientID: string;
  reportID: string;
}) =>
  createSelector(
    reportsModel.selectors.selectEntities,
    reportsModel.selectors.selectCurrentReport,
    reportsModel.selectors.selectDataStream,
    assetsModel.selectors.selectAll,
    assetsModel.selectors.selectPatientAssetsSnapshot,
    assetsModel.selectors.selectPatientAssetsSnapshotCompletedGeneration,
    assetsModel.selectors.selectReportAssetsSnapshot,
    assetsModel.selectors
      .selectReportAssetsSnapshotPatientCompletionAtRefreshStart,
    conditionModel.selectors.selectReportConditionsSnapshot,
    (
      reportEntities,
      currentReport,
      reportDataStream,
      assets,
      patientAssetsSnapshot,
      patientAssetsSnapshotCompletedGeneration,
      reportAssetsSnapshot,
      reportAssetsSnapshotPatientCompletionAtRefreshStart,
      reportConditionsSnapshot,
    ): DCCTVolumeState => {
      if (!patientID || !reportID) {
        return {
          status: 'pending',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      const report =
        reportEntities[reportID] ??
        (currentReport?.ID === reportID ? currentReport : undefined);

      const usableReport =
        report && !report.Deleted?.Deleted && report.PatientID === patientID
          ? report
          : undefined;

      const volumeFile = usableReport
        ? findDCCTVolumeFile(assets, usableReport)
        : null;

      if (volumeFile?.ID) {
        return {
          status: 'ready',
          volumeFileID: volumeFile.ID,
          // int64 arrives as bigint; the engine needs a plain number of bytes.
          volumeFileSizeBytes: volumeFile.Size ? Number(volumeFile.Size) : null,
        };
      }

      if (
        reportDataStream.reportID !== reportID ||
        reportDataStream.loading === 'idle' ||
        reportDataStream.loading === 'pending'
      ) {
        return {
          status: 'pending',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (reportDataStream.loading === 'failed') {
        return {
          status: 'failed',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (!usableReport) {
        return {
          status: 'failed',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (
        (reportAssetsSnapshot.reportID === reportID &&
          reportAssetsSnapshot.loading === 'failed') ||
        (reportConditionsSnapshot.reportID === reportID &&
          reportConditionsSnapshot.loading === 'failed')
      ) {
        return {
          status: 'failed',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (
        reportAssetsSnapshot.reportID !== reportID ||
        reportAssetsSnapshot.loading !== 'succeeded' ||
        reportConditionsSnapshot.reportID !== reportID ||
        reportConditionsSnapshot.loading !== 'succeeded'
      ) {
        return {
          status: 'pending',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (
        patientAssetsSnapshot.patientID !== patientID ||
        patientAssetsSnapshot.loading === 'idle' ||
        patientAssetsSnapshot.loading === 'pending'
      ) {
        return {
          status: 'pending',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (patientAssetsSnapshot.loading === 'failed') {
        return {
          status: 'failed',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      if (
        reportAssetsSnapshotPatientCompletionAtRefreshStart !== null &&
        patientAssetsSnapshotCompletedGeneration ===
          reportAssetsSnapshotPatientCompletionAtRefreshStart
      ) {
        return {
          status: 'pending',
          volumeFileID: null,
          volumeFileSizeBytes: null,
        };
      }

      return {
        status: 'missing',
        volumeFileID: null,
        volumeFileSizeBytes: null,
      };
    },
  );

export const selectDCCTVolumeStatus = (params: {
  patientID: string;
  reportID: string;
}) =>
  createSelector(
    selectDCCTVolumeState(params),
    (volumeState): DCCTVolumeStatus => volumeState.status,
  );
